import React from "react"

import { Skeleton } from "@/components/ui/skeleton"

// Skeleton for Daily Schedule View
export const DailyScheduleSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      {/* Status Summary Cards - Full Width */}
      <div className="grid grid-cols-6 gap-4 w-full">
        {[1, 2, 3, 4, 5, 6].map((card) => (
          <div key={card} className="bg-white border rounded-lg p-4 space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-8 w-12" />
          </div>
        ))}
      </div>

      {/* Controls skeleton */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
        <Skeleton className="h-9 w-40" />
      </div>

      {/* Area sections skeleton */}
      {[1, 2, 3].map((section) => (
        <div key={section} className="border rounded-lg overflow-hidden bg-white">
          {/* Area header - matching list view style */}
          <div className="bg-palette-jungleGreen000 px-4 py-3 border-b cursor-pointer hover:bg-palette-jungleGreen100 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-5 w-5" /> {/* Chevron icon */}
                <Skeleton className="h-6 w-40" /> {/* Area name */}
              </div>
              <Skeleton className="h-5 w-32" /> {/* Count badge */}
            </div>
          </div>

          {/* Shift cards grid */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((card) => (
              <div
                key={card}
                className="border rounded-lg p-3 space-y-2.5 bg-white hover:shadow-md transition-shadow"
              >
                {/* Time and status */}
                <div className="flex items-start justify-between">
                  <Skeleton className="h-5 w-24" /> {/* Time */}
                  <Skeleton className="h-5 w-16 rounded-full" /> {/* Status badge */}
                </div>

                {/* Person name */}
                <Skeleton className="h-5 w-32" />

                {/* Location */}
                <div className="flex items-center gap-1.5">
                  <Skeleton className="h-4 w-4" /> {/* Icon */}
                  <Skeleton className="h-4 w-28" />
                </div>

                {/* Role/Employer */}
                <Skeleton className="h-4 w-20" />

                {/* Badges */}
                <div className="flex gap-2 flex-wrap">
                  <Skeleton className="h-5 w-14 rounded-full" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Skeleton for Weekly Schedule View (Kanban)
export const WeeklyScheduleSkeleton: React.FC = () => {
  return (
    <div className="space-y-3">
      {[0, 1].map((groupIndex) => (
        <div
          key={`loading-group-${groupIndex}`}
          className="border rounded-lg overflow-hidden animate-pulse"
        >
          <div className="bg-palette-jungleGreen000 px-4 py-3 border-b">
            <div className="h-5 w-52 bg-palette-jungleGreen200 rounded" />
          </div>
          <div className="p-2 space-y-2">
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 7 }).map((_, dayIndex) => (
                <div
                  key={`loading-count-${groupIndex}-${dayIndex}`}
                  className="h-8 rounded-md bg-palette-jungleGreen200"
                />
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2 items-start">
              {Array.from({ length: 7 }).map((_, dayIndex) => (
                <div
                  key={`loading-card-${groupIndex}-${dayIndex}`}
                  className="h-24 rounded-md bg-palette-jungleGreen100 border border-palette-jungleGreen200"
                />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// Skeleton for Monthly Schedule View
export const MonthlyScheduleSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      {/* Calendar grid */}
      <div className="border rounded-lg overflow-hidden">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 bg-palette-jungleGreen000 border-b">
          {[1, 2, 3, 4, 5, 6, 7].map((day) => (
            <div key={day} className="p-2 text-center border-r last:border-r-0">
              <Skeleton className="h-4 w-8 mx-auto" />
            </div>
          ))}
        </div>

        {/* Calendar days */}
        {[1, 2, 3, 4, 5].map((week) => (
          <div key={week} className="grid grid-cols-7 border-b last:border-b-0">
            {[1, 2, 3, 4, 5, 6, 7].map((day) => (
              <div key={day} className="min-h-[100px] p-2 border-r last:border-r-0 space-y-1">
                <Skeleton className="h-5 w-6" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// Skeleton for List View
export const ListViewSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      {/* Search bar skeleton */}
      <div className="flex items-center gap-4 mb-4">
        <Skeleton className="h-10 flex-1" />
      </div>

      {/* Table skeleton */}
      <div className="border rounded-lg overflow-hidden">
        {/* Table header */}
        <div className="bg-palette-jungleGreen000 border-b">
          <div className="grid grid-cols-7 gap-4 p-3">
            {[1, 2, 3, 4, 5, 6, 7].map((col) => (
              <Skeleton key={col} className="h-4 w-full" />
            ))}
          </div>
        </div>

        {/* Table rows */}
        {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
          <div key={row} className="border-b last:border-b-0">
            <div className="grid grid-cols-7 gap-4 p-3">
              {[1, 2, 3, 4, 5, 6, 7].map((col) => (
                <Skeleton key={col} className="h-4 w-full" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Skeleton for HR View
export const HRViewSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      {/* Header skeleton */}
      <div className="bg-palette-jungleGreen000 px-4 py-3 rounded-lg border">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-5 w-32" />
        </div>
      </div>

      {/* Team sections skeleton */}
      {[1, 2, 3].map((team) => (
        <div key={team} className="border rounded-lg overflow-hidden">
          {/* Team header */}
          <div className="bg-palette-jungleGreen000 px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-5 w-20" />
            </div>
          </div>

          {/* Table skeleton */}
          <div className="overflow-x-auto">
            <div className="min-w-full">
              {/* Table header */}
              <div className="bg-palette-jungleGreen000 border-b">
                <div className="grid grid-cols-8 gap-2 p-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
                    <Skeleton key={col} className="h-4 w-full" />
                  ))}
                </div>
              </div>

              {/* Table rows */}
              {[1, 2, 3, 4].map((row) => (
                <div key={row} className="border-b last:border-b-0">
                  <div className="grid grid-cols-8 gap-2 p-3">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
                      <Skeleton key={col} className="h-4 w-full" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
